import jsPDF from "jspdf"
import "jspdf-autotable"
import logoUrl from "../assets/images/Logo-edify-no-bg.png"
import { getMaterialsCatalogPricesUpdatedShortLabel } from "../constants/calculadoraDeMateriales.ts"

export type EstimationPdfMaterialRow = {
  name: string
  category: string
  unit: string
  quantity: number
  price?: number | null
}

type JsPdfWithAutoTable = jsPDF & {
  autoTable: (options: Record<string, unknown>) => void
  lastAutoTable?: { finalY: number }
}

type LoadedLogo = {
  dataUrl: string
  width: number
  height: number
}

const BRAND_COLOR = [35, 75, 162] as const
const BRAND_LIGHT = [239, 246, 255] as const
const SLATE_700 = [51, 65, 85] as const
const SLATE_500 = [100, 116, 139] as const
const SLATE_400 = [148, 163, 184] as const
const SLATE_200 = [226, 232, 240] as const
const SLATE_50 = [248, 250, 252] as const

const MARGIN = 14
const FOOTER_HEIGHT = 18
const LOGO_TARGET_WIDTH_MM = 30
const WATERMARK_TEXT = "No constituye oferta"

const unitToShow = (unit: string): string => {
  const map: Record<string, string> = {
    m2: "Metro cuadrado",
    m3: "Metro cúbico",
    kg: "Kilogramo",
    ml: "Mililitro",
    cm: "Centímetro",
    u: "Unidad",
    l: "Litro",
    cm2: "Centímetro cuadrado",
    cm3: "Centímetro cúbico",
    gr: "Gramo",
    m: "Metro",
  }
  return map[unit] ?? unit
}

const formatCurrency = (value: number): string => {
  if (value == null || Number.isNaN(Number(value))) return "-"
  return new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS" }).format(value)
}

const formatDate = () => {
  const d = new Date()
  return d.toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric" })
}

const formatDateTime = () =>
  new Date().toLocaleString("es-AR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })

function loadLogoAsset(src: string): Promise<LoadedLogo | null> {
  return new Promise((resolve) => {
    const img = new Image()
    img.onload = () => {
      const naturalWidth = img.naturalWidth || img.width
      const naturalHeight = img.naturalHeight || img.height
      if (!naturalWidth || !naturalHeight) {
        resolve(null)
        return
      }
      const canvas = document.createElement("canvas")
      canvas.width = naturalWidth
      canvas.height = naturalHeight
      const ctx = canvas.getContext("2d")
      if (!ctx) {
        resolve(null)
        return
      }
      ctx.drawImage(img, 0, 0)
      resolve({
        dataUrl: canvas.toDataURL("image/png"),
        width: naturalWidth,
        height: naturalHeight,
      })
    }
    img.onerror = () => resolve(null)
    img.src = src
  })
}

function drawWatermark(doc: jsPDF, pageWidth: number, pageHeight: number): void {
  doc.setFont("helvetica", "normal")
  doc.setFontSize(10)
  doc.setTextColor(242, 245, 248)
  doc.text(WATERMARK_TEXT, pageWidth / 2, pageHeight * 0.68, {
    align: "center",
    angle: 28,
  })
}

function drawFooter(doc: jsPDF, pageWidth: number, pageHeight: number): void {
  const footerY = pageHeight - FOOTER_HEIGHT + 4

  doc.setDrawColor(SLATE_200[0], SLATE_200[1], SLATE_200[2])
  doc.setLineWidth(0.3)
  doc.line(MARGIN, footerY - 4, pageWidth - MARGIN, footerY - 4)

  doc.setFont("helvetica", "normal")
  doc.setFontSize(8)
  doc.setTextColor(SLATE_400[0], SLATE_400[1], SLATE_400[2])
  doc.text("Edify — Estimación de costos de materiales", MARGIN, footerY + 2)
  doc.text(`Pág. ${doc.getNumberOfPages()}`, pageWidth - MARGIN, footerY + 2, { align: "right" })
}

function drawPageFooter(doc: jsPDF): void {
  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()
  drawFooter(doc, pageWidth, pageHeight)
}

function drawPageBackground(doc: jsPDF): void {
  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()
  drawWatermark(doc, pageWidth, pageHeight)
}

function drawDisclaimerBox(doc: jsPDF, x: number, y: number, width: number): number {
  const paddingX = 4
  const paddingY = 3.5
  const line1 = "Documento de referencia. Los importes provienen del catálogo Edify y son orientativos."
  const line2 = "No constituye oferta comercial ni presupuesto formal por parte de Edify ni de proveedores."
  const line3 = getMaterialsCatalogPricesUpdatedShortLabel()

  doc.setFont("helvetica", "normal")
  doc.setFontSize(8.5)
  const wrapped1 = doc.splitTextToSize(line1, width - paddingX * 2)
  const wrapped2 = doc.splitTextToSize(line2, width - paddingX * 2)
  const wrapped3 = doc.splitTextToSize(line3, width - paddingX * 2)
  const textBlockHeight =
    wrapped1.length * 3.8 + wrapped2.length * 3.8 + wrapped3.length * 3.8 + paddingY * 2 + 2
  const boxHeight = Math.max(textBlockHeight, 18)

  doc.setFillColor(BRAND_LIGHT[0], BRAND_LIGHT[1], BRAND_LIGHT[2])
  doc.setDrawColor(191, 219, 254)
  doc.setLineWidth(0.4)
  doc.roundedRect(x, y, width, boxHeight, 2, 2, "FD")

  doc.setTextColor(SLATE_700[0], SLATE_700[1], SLATE_700[2])
  let textY = y + paddingY + 3
  wrapped1.forEach((line: string) => {
    doc.text(line, x + paddingX, textY)
    textY += 3.8
  })
  wrapped2.forEach((line: string) => {
    doc.text(line, x + paddingX, textY)
    textY += 3.8
  })
  doc.setTextColor(SLATE_500[0], SLATE_500[1], SLATE_500[2])
  wrapped3.forEach((line: string) => {
    doc.text(line, x + paddingX, textY)
    textY += 3.8
  })

  return y + boxHeight
}

function drawDocumentHeader(doc: JsPdfWithAutoTable, logo: LoadedLogo | null): number {
  const pageWidth = doc.internal.pageSize.getWidth()
  const contentWidth = pageWidth - MARGIN * 2
  let cursorY = MARGIN

  if (logo) {
    const logoHeightMm = LOGO_TARGET_WIDTH_MM * (logo.height / logo.width)
    doc.addImage(logo.dataUrl, "PNG", MARGIN, cursorY, LOGO_TARGET_WIDTH_MM, logoHeightMm)
    cursorY += logoHeightMm + 4
  }

  doc.setFont("helvetica", "bold")
  doc.setFontSize(18)
  doc.setTextColor(BRAND_COLOR[0], BRAND_COLOR[1], BRAND_COLOR[2])
  doc.text("Estimación de costos", MARGIN, cursorY + 5)

  doc.setFont("helvetica", "normal")
  doc.setFontSize(10)
  doc.setTextColor(SLATE_500[0], SLATE_500[1], SLATE_500[2])
  doc.text(`Generado el ${formatDateTime()}`, MARGIN, cursorY + 12)

  cursorY += 18

  doc.setDrawColor(BRAND_COLOR[0], BRAND_COLOR[1], BRAND_COLOR[2])
  doc.setLineWidth(0.6)
  doc.line(MARGIN, cursorY, pageWidth - MARGIN, cursorY)
  cursorY += 6

  cursorY = drawDisclaimerBox(doc, MARGIN, cursorY, contentWidth)
  return cursorY + 8
}

export async function saveEstimationCostPdf(materials: EstimationPdfMaterialRow[]): Promise<void> {
  const logo = await loadLogoAsset(logoUrl)
  const doc = new jsPDF() as JsPdfWithAutoTable
  const pageWidth = doc.internal.pageSize.getWidth()
  const contentWidth = pageWidth - MARGIN * 2

  const tableStartY = drawDocumentHeader(doc, logo)

  const tableBody =
    materials?.map((material) => {
      const price = Number(material.price) || 0
      const quantity = Number(material.quantity) || 0
      const subtotal = price * quantity
      return [
        material.name,
        material.category,
        String(quantity),
        unitToShow(material.unit || ""),
        formatCurrency(price),
        formatCurrency(subtotal),
      ]
    }) ?? []

  const total =
    materials?.reduce((sum, m) => sum + (Number(m.price) || 0) * (Number(m.quantity) || 1), 0) ?? 0

  const materialCount = materials?.length ?? 0
  const materialLabel = materialCount === 1 ? "1 material" : `${materialCount} materiales`

  doc.setFont("helvetica", "normal")
  doc.setFontSize(10)
  doc.setTextColor(SLATE_700[0], SLATE_700[1], SLATE_700[2])
  doc.text(`Listado analizado: ${materialLabel}`, MARGIN, tableStartY - 2)

  doc.autoTable({
    head: [["Material", "Categoría", "Cant.", "Unidad", "Precio unit.", "Subtotal"]],
    body: tableBody,
    startY: tableStartY + 2,
    theme: "plain",
    margin: { left: MARGIN, right: MARGIN, bottom: FOOTER_HEIGHT + 4 },
    tableWidth: contentWidth,
    headStyles: {
      fillColor: BRAND_COLOR,
      textColor: [255, 255, 255],
      fontSize: 9,
      fontStyle: "bold",
      cellPadding: 3,
    },
    bodyStyles: {
      fontSize: 8.5,
      textColor: SLATE_700,
      cellPadding: 2.8,
      lineColor: SLATE_200,
      lineWidth: 0.2,
    },
    alternateRowStyles: {
      fillColor: SLATE_50,
    },
    columnStyles: {
      0: { cellWidth: contentWidth * 0.28 },
      1: { cellWidth: contentWidth * 0.22 },
      2: { halign: "right", cellWidth: contentWidth * 0.08 },
      3: { cellWidth: contentWidth * 0.14 },
      4: { halign: "right", cellWidth: contentWidth * 0.14 },
      5: { halign: "right", cellWidth: contentWidth * 0.14 },
    },
    didDrawPage: () => {
      drawPageFooter(doc)
    },
    willDrawPage: () => {
      drawPageBackground(doc)
    },
  })

  const lastTable = doc.lastAutoTable
  let summaryY = lastTable ? lastTable.finalY + 8 : tableStartY + 24

  if (summaryY > doc.internal.pageSize.getHeight() - FOOTER_HEIGHT - 24) {
    doc.addPage()
    drawPageBackground(doc)
    summaryY = MARGIN + 6
  }

  doc.setFillColor(BRAND_LIGHT[0], BRAND_LIGHT[1], BRAND_LIGHT[2])
  doc.setDrawColor(BRAND_COLOR[0], BRAND_COLOR[1], BRAND_COLOR[2])
  doc.setLineWidth(0.5)
  doc.roundedRect(MARGIN, summaryY, contentWidth, 14, 2, 2, "FD")

  doc.setFont("helvetica", "bold")
  doc.setFontSize(11)
  doc.setTextColor(SLATE_700[0], SLATE_700[1], SLATE_700[2])
  doc.text("Total estimado", MARGIN + 4, summaryY + 9)
  doc.setTextColor(BRAND_COLOR[0], BRAND_COLOR[1], BRAND_COLOR[2])
  doc.text(formatCurrency(total), pageWidth - MARGIN - 4, summaryY + 9, { align: "right" })

  drawPageFooter(doc)

  const fileName = `estimacion-costos-edify-${formatDate().replace(/\//g, "-")}.pdf`
  doc.save(fileName)
}
